/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import { Button } from '@motorway/mw-highway-code';

import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayMovePageEvents } from 'Services/analytics/events/motorwayMoveEvents';
import { sendBrazeMotorwayMoveBookDeliveryEvent } from 'Services/braze/brazeEvents';
import { AccountCategory, AccountSlugs } from 'Types/account';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import ROUTES from 'Utilities/routes';

import { getDiscoverMotorwayMoveButtonParams } from './HeroSection/HeroSection.helpers';
import { OPEN_REQUEST_CALLBACK_MODAL_HASH } from './DiscoverMWMove.consts';
import type { DiscoverMWMoveProps } from './DiscoverMWMove.types';
import { DiscoverMWMoveEmitter } from './DiscoverMWMoveEmitter';
import type { HeroSectionProps } from './HeroSection';
import { type ModalRequestCallbackProps, useRequestCallback } from './ModalRequestCallback';

export const useStickyFooterObserver = ({ heroRef }: { heroRef: RefObject<HTMLDivElement> }) => {
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				DiscoverMWMoveEmitter.emit('showSticky', !entry.isIntersecting);
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0,
			},
		);

		if (heroRef.current) {
			observer.observe(heroRef.current);
		}
		return () => {
			observer?.disconnect();
		};
	}, [heroRef]);
};

export const useAutoOpenModal = ({
	isLoggedOut,
	openRequestCallbackModal,
}: {
	isLoggedOut: boolean;
	openJoinModal?: () => void;
	openRequestCallbackModal?: () => void;
}) => {
	useEffect(() => {
		if (isLoggedOut) {
			window.location.hash = '';

			return undefined;
		}

		const { hash } = window.location;
		const hashStr = hash.substring(1);

		if (hashStr === OPEN_REQUEST_CALLBACK_MODAL_HASH) {
			openRequestCallbackModal?.();
		}

		window.location.hash = '';

		return () => {
			window.location.hash = '';
		};
	}, [isLoggedOut, openRequestCallbackModal]);
};

export const useDiscoverMWMove = ({ displayAs, requestCallbackCallback, withValidatePhone }: DiscoverMWMoveProps) => {
	const heroRef = useRef<HTMLDivElement | null>(null);
	const isLoggedOut = displayAs === 'signedOut';

	const {
		closeModal: closeModalRequestCallback,
		onSubmit: onRequestCallbackSubmit,
		openModal: openRequestCallbackModal,
		state: requestCallbackModalState,
		user,
	} = useRequestCallback({ callback: requestCallbackCallback, withValidatePhone });

	useStickyFooterObserver({ heroRef });

	useAutoOpenModal({ isLoggedOut, openRequestCallbackModal });

	const [hasStickyButtons, setHasStickyButtons] = useState<boolean>(false);
	const callbackEnabled = useFeatureToggle(FEATURES.motorwayMoveCallback);

	useEffect(() => {
		DiscoverMWMoveEmitter.on('showSticky', setHasStickyButtons);

		return () => {
			DiscoverMWMoveEmitter.off('showSticky', setHasStickyButtons);
		};
	}, []);

	const onDeliveryBookingClick = () => {
		void sendBrazeMotorwayMoveBookDeliveryEvent();
		motorwayMovePageEvents<{
			label: string | number;
		}>({
			customData: {
				label: hasStickyButtons ? 'sticky_footer' : 'hero_section',
			},
			name: `Motorway Move Book delivery button clicked${isLoggedOut ? ' logout' : ''}`,
			schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
			version: SNOWPLOW_EVENTS.CLICK.VERSION,
		});
	};

	const requestCallbackButtonProps = getDiscoverMotorwayMoveButtonParams({
		hash: OPEN_REQUEST_CALLBACK_MODAL_HASH,
		isLoggedOut,
		onClick: () => {
			motorwayMovePageEvents<{
				label: string | number;
			}>({
				customData: {
					label: hasStickyButtons ? 'sticky_footer' : 'hero_section',
				},
				name: `Motorway Move Request a call back button clicked${isLoggedOut ? ' logout' : ''}`,
				schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
				version: SNOWPLOW_EVENTS.CLICK.VERSION,
			});

			if (!isLoggedOut) {
				openRequestCallbackModal?.();
			}
		},
	});

	const actions = (
		<>
			<Link
				legacyBehavior
				passHref
				as={ROUTES.ACCOUNT.as(AccountCategory.PURCHASES, AccountSlugs.COMPLETE)}
				href={{ pathname: ROUTES.ACCOUNT.href, search: '?purchasesCompleteStatusType=checksComplete' }}
			>
				<Button label="Book a delivery" onClick={onDeliveryBookingClick} variant="primary" />
			</Link>
			{callbackEnabled && (
				<Button
					data-cy="mw-move-request-callback"
					label="Request a call back"
					variant="secondary"
					{...requestCallbackButtonProps}
				/>
			)}
		</>
	);

	const heroProps: HeroSectionProps = {
		actions,
		hasStickyButtons,
		ref: heroRef,
	};

	const requestCallbackProps: ModalRequestCallbackProps = {
		defaultValues: user,
		onDismiss: closeModalRequestCallback,
		onSubmit: onRequestCallbackSubmit,
		status: requestCallbackModalState,
	};

	return {
		heroProps,
		requestCallbackProps,
		showRequestCallbackModal: requestCallbackModalState !== 'closed',
	};
};
